/**
 * Shared construction for every currency Intl.NumberFormat in the app, so locale and
 * currencyDisplay defaults can't drift between call sites. Defaults to "narrowSymbol" (bare "$"
 * for AUD/USD/NZD/CAD) - only safe where the ISO code is shown alongside, so pass
 * currencyDisplay: "symbol" (renders "A$") for surfaces that don't. Pass the app's LOCALE_ID
 * rather than leaving it undefined (browser locale), so formatting stays consistent with
 * DatePipe/DecimalPipe, which resolve against LOCALE_ID rather than the browser's locale.
 * Callers that format the same currency repeatedly (chart ticks, table rows) should cache the
 * returned formatter rather than rebuilding it per value - Intl.NumberFormat construction isn't
 * free.
 */
export function buildCurrencyFormatter(
  currency: string,
  locale: string,
  options?: Partial<Intl.NumberFormatOptions>,
): Intl.NumberFormat {
  return new Intl.NumberFormat(locale, {
    currencyDisplay: "narrowSymbol",
    ...options,
    style: "currency",
    currency,
  });
}

/**
 * Locale-aware currency string, e.g. "$3,450.00" for AUD in en-AU/en-US, "€3.450,00" for EUR in
 * de-DE, "¥3,450" for JPY (no decimals).
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string,
  locale: string,
  options?: Partial<Intl.NumberFormatOptions>,
): string {
  return buildCurrencyFormatter(currency, locale, options).format(amount);
}

/**
 * Just the currency symbol/ISO abbreviation Intl would render for this currency+locale (e.g.
 * "$" narrow or "A$" non-narrow for AUD), for surfaces that build their own compact layout
 * around a bare symbol rather than using a fully-formatted amount.
 */
export function getCurrencySymbol(
  currency: string,
  locale: string,
  currencyDisplay: Intl.NumberFormatOptions["currencyDisplay"] = "narrowSymbol",
): string {
  return (
    buildCurrencyFormatter(currency, locale, { currencyDisplay })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? ""
  );
}

export function getDigitCount(amount: number | undefined | null): number {
  const absoluteAmount = Math.abs(amount ?? 0);
  const normalizedAmount = new Intl.NumberFormat("en-US", {
    useGrouping: false,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absoluteAmount);

  return normalizedAmount.replace(/\D/g, "").length;
}

/** Guards untyped number inputs (e.g. backend payloads, localStorage JSON): a real, finite number. */
export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
